import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Package,
  Truck,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  RefreshCw,
  Building2,
  Boxes,
  ChevronDown,
  ChevronUp,
  X,
  Search,
  Maximize2,
  Smartphone
} from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

// Hook for batch grouping (15+ minute gaps)
const useDeliveryBatches = (entries) => {
  return React.useMemo(() => {
    if (!entries || entries.length === 0) return [];

    const BATCH_GAP_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
    const batches = [];
    let currentBatch = null;

    // Sort entries by time descending (most recent first)
    const sorted = [...entries].sort((a, b) =>
      new Date(b.TIME_OF_SCAN) - new Date(a.TIME_OF_SCAN)
    );

    sorted.forEach((entry) => {
      const entryTime = new Date(entry.TIME_OF_SCAN).getTime();

      if (!currentBatch) {
        currentBatch = {
          entries: [entry],
          startTime: entryTime,
          endTime: entryTime
        };
      } else {
        const timeDiff = currentBatch.endTime - entryTime;

        if (timeDiff <= BATCH_GAP_MS) {
          currentBatch.entries.push(entry);
          currentBatch.endTime = entryTime;
        } else {
          batches.push(currentBatch);
          currentBatch = {
            entries: [entry],
            startTime: entryTime,
            endTime: entryTime
          };
        }
      }
    });

    if (currentBatch) {
      batches.push(currentBatch);
    }

    return batches;
  }, [entries]);
};

// Format time for display (DB already stores EST)
const formatTimeEST = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Manual Entry Modal Component
const ManualEntryModal = ({ isOpen, onClose, onSubmit, clients }) => {
  const [type, setType] = useState('Boxes');
  const [quantity, setQuantity] = useState(1);
  const [company, setCompany] = useState('');
  const [supplier, setSupplier] = useState('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredClients = clients.filter(client =>
    (client.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!company) return;
    setIsSubmitting(true);
    await onSubmit({ type, quantity, company, supplier, notes });
    setIsSubmitting(false);
    // Reset form
    setType('Boxes');
    setQuantity(1);
    setCompany('');
    setSupplier('');
    setNotes('');
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-lg">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Manual Entry</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* Type Toggle */}
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-3">Type</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setType('Boxes')}
                className={`py-6 px-4 text-xl font-bold rounded-lg border-4 transition-all ${
                  type === 'Boxes'
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <Boxes className="w-10 h-10 mx-auto mb-2" />
                BOXES
              </button>
              <button
                onClick={() => setType('Pallet')}
                className={`py-6 px-4 text-xl font-bold rounded-lg border-4 transition-all ${
                  type === 'Pallet'
                    ? 'bg-purple-100 border-purple-500 text-purple-700'
                    : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
              >
                <Truck className="w-10 h-10 mx-auto mb-2" />
                PALLET
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-3">Quantity</label>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-16 h-16 text-3xl font-bold bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                -
              </button>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 h-16 text-center text-3xl font-bold border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:ring-2 focus:ring-gray-500"
              />
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-16 h-16 text-3xl font-bold bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                +
              </button>
            </div>
          </div>

          {/* Client Selection */}
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-3">Client *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCompany('');
                }}
                className="w-full pl-10 pr-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:ring-2 focus:ring-gray-500"
              />
            </div>
            {searchTerm && (
              <div className="mt-2 max-h-40 overflow-y-auto border-2 border-gray-200 rounded-lg">
                {filteredClients.length === 0 ? (
                  <div className="p-3 text-gray-500 text-center">No clients found</div>
                ) : (
                  filteredClients.slice(0, 10).map((client, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCompany(client.name);
                        setSearchTerm(client.name);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-100 border-b last:border-b-0 ${
                        company === client.name ? 'bg-blue-50' : ''
                      }`}
                    >
                      <span className="font-medium">{client.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
            {company && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-green-700 font-medium">Selected: {company}</span>
              </div>
            )}
          </div>

          {/* Supplier */}
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-3">Supplier (Optional)</label>
            <input
              type="text"
              placeholder="Enter supplier name..."
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:ring-2 focus:ring-gray-500"
            />
          </div>

          {/* Notes */}
          <div className="mb-8">
            <label className="block text-lg font-medium text-gray-700 mb-3">Notes (Optional)</label>
            <textarea
              placeholder="Enter any notes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:ring-2 focus:ring-gray-500 resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!company || isSubmitting}
            variant="primary"
            className="w-full py-4 text-xl font-bold"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Recording...
              </>
            ) : (
              <>
                <Plus className="w-6 h-6 mr-2" />
                Add {quantity} {type}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Main Component
function DeliveryReceiving({ standalone = false }) {
  const [scanInput, setScanInput] = useState('');
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [lastScan, setLastScan] = useState(null);
  const [flashSuccess, setFlashSuccess] = useState(false);
  const [flashError, setFlashError] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [clients, setClients] = useState([]);
  const [expandedBatches, setExpandedBatches] = useState({});

  const inputRef = useRef(null);
  const successSound = useRef(null);
  const errorSound = useRef(null);

  const batches = useDeliveryBatches(entries);

  // Initialize sounds
  useEffect(() => {
    successSound.current = new Audio('/sounds/success-scan.mp3');
    errorSound.current = new Audio('/sounds/error-scan.mp3');
    successSound.current.volume = 0.7;
    errorSound.current.volume = 0.7;
  }, []);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // Fetch delivery log
  const fetchLog = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/delivery-receiving/log`);
      if (response.data.success) {
        setEntries(response.data.entries);
      }
    } catch (error) {
      console.error('Error fetching delivery log:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch clients for manual entry
  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/get_clients`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchLog();
    fetchClients();
  }, [fetchLog, fetchClients]);

  // Auto-focus input
  useEffect(() => {
    if (inputRef.current && !showManualEntry) {
      inputRef.current.focus();
    }
  }, [showManualEntry]);

  // Handle scan submission
  const handleScan = async (trackingCode) => {
    if (!trackingCode.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/api/delivery-receiving/scan`, {
        tracking_code: trackingCode.trim()
      });

      if (response.data.success) {
        // Success (including duplicates - no error sound for duplicates)
        successSound.current?.play().catch(() => {});
        const isDuplicate = response.data.duplicate;
        setLastScan({
          code: trackingCode.trim(),
          success: true,
          duplicate: isDuplicate
        });
        setFlashSuccess(true);
        setTimeout(() => setFlashSuccess(false), 500);
        showNotification(
          isDuplicate ? 'Already scanned (kept original time)' : 'Scan recorded successfully',
          'success'
        );
        if (!isDuplicate) {
          fetchLog(); // Only refresh if new entry
        }
      } else {
        // Error
        errorSound.current?.play().catch(() => {});
        setLastScan({ code: trackingCode.trim(), success: false, message: response.data.message });
        setFlashError(true);
        setTimeout(() => setFlashError(false), 500);
        showNotification(response.data.message || 'Failed to record scan', 'error');
      }
    } catch (error) {
      errorSound.current?.play().catch(() => {});
      const message = error.response?.data?.message || 'Error recording scan';
      setLastScan({ code: trackingCode.trim(), success: false, message });
      setFlashError(true);
      setTimeout(() => setFlashError(false), 500);
      showNotification(message, 'error');
    }

    setScanInput('');
    inputRef.current?.focus();
  };

  // Handle input key press (Enter or ;)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ';') {
      e.preventDefault();
      const value = scanInput.replace(/;$/, '').trim();
      if (value) {
        handleScan(value);
      }
    }
  };

  // Handle manual entry submission
  const handleManualSubmit = async ({ type, quantity, company, supplier, notes }) => {
    try {
      const response = await axios.post(`${API_URL}/api/delivery-receiving/manual`, {
        type,
        quantity,
        company,
        supplier,
        notes
      });

      if (response.data.success) {
        successSound.current?.play().catch(() => {});
        showNotification(response.data.message, 'success');
        fetchLog();
      } else {
        errorSound.current?.play().catch(() => {});
        showNotification(response.data.message || 'Failed to record manual entry', 'error');
      }
    } catch (error) {
      errorSound.current?.play().catch(() => {});
      showNotification(error.response?.data?.message || 'Error recording manual entry', 'error');
    }
  };

  // Toggle batch expansion
  const toggleBatch = (batchIndex) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchIndex]: !prev[batchIndex]
    }));
  };

  // Check if entry is manual
  const isManualEntry = (entry) => entry.TRACKING_CODE?.startsWith('MANUAL_ENTRY');

  // Get entry display info
  const getEntryInfo = (entry) => {
    if (isManualEntry(entry)) {
      const match = entry.TRACKING_CODE.match(/MANUAL_ENTRY_\d+-(Boxes|Pallet)/i);
      return {
        type: match ? match[1] : 'Manual',
        isManual: true
      };
    }
    return { type: 'Scanned', isManual: false };
  };

  // Count items in batch by type
  const getBatchCounts = (batch) => {
    const counts = { boxes: 0, pallets: 0, scans: 0 };
    batch.entries.forEach(entry => {
      if (entry.TRACKING_CODE?.toLowerCase().includes('boxes')) counts.boxes++;
      else if (entry.TRACKING_CODE?.toLowerCase().includes('pallet')) counts.pallets++;
      else counts.scans++;
    });
    return counts;
  };

  const containerClass = standalone
    ? 'min-h-screen bg-gray-100 p-4 md:p-8'
    : 'min-h-screen bg-gray-50 p-6';

  return (
    <div className={containerClass}>
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Manual Entry Modal */}
      <ManualEntryModal
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onSubmit={handleManualSubmit}
        clients={clients}
      />

      <div className={standalone ? 'max-w-4xl mx-auto' : ''}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              DELIVERY RECEIVING
            </h1>
            <div className="flex items-center space-x-3">
              {!standalone && (
                <>
                  <Button
                    onClick={() => window.open('/pda-scanner', '_blank')}
                    variant="ghost"
                    className="py-3 px-4 text-lg font-semibold"
                    title="Open PDA scanner for handheld terminals"
                  >
                    <Smartphone className="w-5 h-5 mr-2" />
                    PDA Mode
                  </Button>
                  <Button
                    onClick={() => window.open('/delivery-receiving', '_blank')}
                    variant="ghost"
                    className="py-3 px-4 text-lg font-semibold"
                    title="Open fullscreen for tablet"
                  >
                    <Maximize2 className="w-5 h-5 mr-2" />
                    Fullscreen
                  </Button>
                </>
              )}
              <Button
                onClick={() => setShowManualEntry(true)}
                variant="outline"
                className="py-3 px-6 text-lg font-semibold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Manual Entry
              </Button>
            </div>
          </div>
          <p className="text-lg text-gray-600">Scan packages or enter manually</p>
        </div>

        {/* Scan Input Section */}
        <Card className={`p-6 mb-6 transition-colors duration-300 ${
          flashSuccess ? 'bg-green-100 border-green-500' :
          flashError ? 'bg-red-100 border-red-500' : ''
        }`}>
          <label className="block text-xl font-semibold text-gray-700 mb-4">
            SCAN TRACKING CODE
          </label>
          <input
            ref={inputRef}
            type="text"
            value={scanInput}
            onChange={(e) => setScanInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Scan or type tracking code..."
            className="w-full text-3xl md:text-4xl font-mono p-6 border-4 border-gray-300 rounded-lg focus:border-gray-900 focus:ring-4 focus:ring-gray-200 transition-all"
            autoComplete="off"
            autoFocus
          />
          <p className="mt-3 text-gray-500">Press Enter or scan to submit</p>

          {/* Last Scan Display */}
          {lastScan && (
            <div className={`mt-4 p-4 rounded-lg flex items-center ${
              lastScan.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
            }`}>
              {lastScan.success ? (
                <CheckCircle2 className="w-8 h-8 text-green-600 mr-4 flex-shrink-0" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600 mr-4 flex-shrink-0" />
              )}
              <div>
                <div className={`text-xl font-bold ${lastScan.success ? 'text-green-700' : 'text-red-700'}`}>
                  {lastScan.code}
                </div>
                <div className={`text-sm ${lastScan.success ? 'text-green-600' : 'text-red-600'}`}>
                  {lastScan.success
                    ? (lastScan.duplicate ? 'Already scanned (original time kept)' : 'Recorded successfully')
                    : lastScan.message}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Delivery Log */}
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Today's Deliveries</h2>
              <p className="text-gray-500">Recent 10 entries grouped by time</p>
            </div>
            <Button
              onClick={fetchLog}
              variant="ghost"
              className="p-2"
              disabled={isLoading}
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" />
              <span className="ml-4 text-lg text-gray-600">Loading...</span>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-700">No deliveries today</h3>
              <p className="text-gray-500">Scan a package to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {batches.map((batch, batchIndex) => {
                const counts = getBatchCounts(batch);
                const isExpanded = expandedBatches[batchIndex] !== false; // Default expanded
                const startTime = formatTimeEST(batch.entries[batch.entries.length - 1].TIME_OF_SCAN);
                const endTime = formatTimeEST(batch.entries[0].TIME_OF_SCAN);
                const timeDisplay = startTime === endTime ? startTime : `${startTime} - ${endTime}`;

                return (
                  <div key={batchIndex} className="bg-white">
                    {/* Batch Header */}
                    <button
                      onClick={() => toggleBatch(batchIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <Clock className="w-7 h-7 text-gray-500 mr-4" />
                        <div className="text-left">
                          <div className="text-2xl font-bold text-gray-900">
                            Batch {batchIndex + 1}
                          </div>
                          <div className="text-lg text-gray-600 font-medium">{timeDisplay} EST</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl font-black text-gray-800">
                          {batch.entries.length}
                        </span>
                        <span className="text-lg text-gray-500 mr-4">items</span>
                        {counts.scans > 0 && (
                          <Badge variant="info" className="py-2 px-3 text-base font-bold">
                            {counts.scans} scanned
                          </Badge>
                        )}
                        {counts.boxes > 0 && (
                          <Badge variant="success" className="py-2 px-3 text-base font-bold">
                            {counts.boxes} boxes
                          </Badge>
                        )}
                        {counts.pallets > 0 && (
                          <Badge variant="warning" className="py-2 px-3 text-base font-bold">
                            {counts.pallets} pallets
                          </Badge>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-6 h-6 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Batch Entries */}
                    {isExpanded && (
                      <div className="px-6 pb-4 space-y-3">
                        {batch.entries.map((entry, entryIndex) => {
                          const info = getEntryInfo(entry);
                          return (
                            <div
                              key={entryIndex}
                              className={`p-5 rounded-xl border-2 ${
                                info.isManual
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  {info.isManual ? (
                                    <Building2 className="w-8 h-8 text-blue-600 mr-4" />
                                  ) : (
                                    <Package className="w-8 h-8 text-gray-600 mr-4" />
                                  )}
                                  <div>
                                    <div className="text-xl font-mono font-bold text-gray-900">
                                      {info.isManual ? (
                                        <>
                                          <span className="text-blue-700">MANUAL</span>
                                          {entry.COMPANY && (
                                            <span className="ml-2 text-gray-800 font-sans">
                                              - {entry.COMPANY}
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        entry.TRACKING_CODE
                                      )}
                                    </div>
                                    {info.isManual && entry.SUPPLIER && (
                                      <div className="text-base text-gray-600 mt-1">
                                        Supplier: {entry.SUPPLIER}
                                      </div>
                                    )}
                                    {entry.NOTES && (
                                      <div className="text-base text-gray-500 mt-1 italic">
                                        Note: {entry.NOTES}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-gray-800">
                                    {formatTimeEST(entry.TIME_OF_SCAN)}
                                  </div>
                                  <Badge variant={info.isManual ? 'info' : 'default'} className="text-sm font-semibold mt-1">
                                    {info.type}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default DeliveryReceiving;
